/**
 * Zod schemas for IPC payload validation.
 * Every IPC handler must validate its input through these schemas
 * before processing, preventing injection and invalid data.
 */
import { z } from 'zod'
import type { Stage1Result, Stage2Result, Stage3Result, Stage4Result, Stage5Result, Stage6Result } from './types'

// ─── Primitives ──────────────────────────────────────────────────────────────

export const ProjectIdSchema = z.uuid('Invalid project ID format')
export const JobIdSchema = z.uuid('Invalid job ID format')

export const AIProviderSchema = z.enum(['claude', 'openai', 'azure-openai'])

export const ApiKeySchema = z.string().min(1, 'API key is required')

// ─── Settings ────────────────────────────────────────────────────────────────

export const SettingsSchema = z.object({
  aiProvider: AIProviderSchema.optional(),
  anthropicApiKey: z.string().optional(),
  openaiApiKey: z.string().optional(),
  azureEndpoint: z.url().optional().or(z.literal('')),
  azureApiKey: z.string().optional(),
  azureDeployment: z.string().optional(),
  azureApiVersion: z.string().optional(),
})

// ─── Client Branding ─────────────────────────────────────────────────────────

const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Invalid hex color')

// The logo travels inside job queue messages (Azure Service Bus Standard tier caps a
// message at 256KB) and is stored inline in the project record, so the cap is sized to
// keep the base64 string comfortably under that limit.
export const LOGO_MAX_FILE_BYTES = 150 * 1024
export const LOGO_MAX_FILE_LABEL = '150KB'
export const LOGO_ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'] as const
// base64 expands 3 bytes to 4 chars; the extra 32 covers the longest data URL prefix.
export const LOGO_DATA_URL_MAX_LENGTH = Math.ceil(LOGO_MAX_FILE_BYTES / 3) * 4 + 32

const LogoDataUrlSchema = z
  .string()
  .max(LOGO_DATA_URL_MAX_LENGTH, `Logo image is too large (max ${LOGO_MAX_FILE_LABEL})`)
  .regex(/^data:image\/(png|jpe?g|svg\+xml);base64,[A-Za-z0-9+/]+=*$/, 'Logo must be a base64-encoded PNG, JPEG, or SVG data URL')

export const ClientBrandingSchema = z.object({
  clientName: z.string().max(200).optional().default(''),
  primaryColor: HexColorSchema.optional().default('#A100FF'),
  primaryColorDark: HexColorSchema.optional().default('#7900B8'),
  primaryColorLight: HexColorSchema.optional().default('#D580FF'),
  accentColor: HexColorSchema.optional(),
  isWip: z.boolean().optional().default(true),
  logoDataUrl: LogoDataUrlSchema.optional(),
  versionLabel: z.string().max(60).optional(),
})

// ─── Client Profile ──────────────────────────────────────────────────────────

export const ClientProfileSchema = z.object({
  companyName: z.string().min(1).max(500),
  industry: z.string().max(200).optional(),
  geography: z.string().max(200).optional(),
  headcountMin: z.number().int().min(0).optional(),
  headcountMax: z.number().int().min(0).optional(),
  targetHeadcount: z.number().int().min(1).max(5_000_000).optional(),
  functionScope: z.array(z.string().min(1).max(200)),
  ambitionLevel: z.number().int().min(1).max(5).optional().default(3),
})

// ─── Generate Payloads ───────────────────────────────────────────────────────

export const GenerateOutsideInPayloadSchema = z.object({
  projectId: ProjectIdSchema,
  profile: ClientProfileSchema,
  branding: ClientBrandingSchema,
  provider: AIProviderSchema,
  apiKey: z.string().default(''),
  sessionId: z.uuid().optional(),
  industryChoices: z.array(z.string().max(200)).optional(),
})

export const BrandColorLookupPayloadSchema = z.object({
  companyName: z.string().min(1).max(200),
  provider: AIProviderSchema,
  apiKey: z.string().default(''),
})

export const GenerateProgressEventSchema = z.object({
  subfunctionName: z.string().min(1).max(500),
  index: z.number().int().min(0),
  total: z.number().int().min(1),
  status: z.enum(['generating', 'done', 'error']),
  error: z.string().max(2000).optional(),
})

export const OutsideInJobStatusSchema = z.enum(['pending', 'running', 'succeeded', 'failed'])

export const OutsideInJobStateSchema = z.object({
  id: JobIdSchema,
  projectId: ProjectIdSchema,
  status: OutsideInJobStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().optional(),
  error: z.string().optional(),
  progress: z.array(GenerateProgressEventSchema),
})

export const InsideOutJobStatusSchema = z.enum(['pending', 'running', 'succeeded', 'failed'])

export const InsideOutJobStateSchema = z.object({
  id: JobIdSchema,
  projectId: ProjectIdSchema,
  status: InsideOutJobStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().optional(),
  error: z.string().optional(),
  progress: z.array(GenerateProgressEventSchema),
})

export const FileEntrySchema = z.object({
  base64: z.string().min(1),
  name: z.string().min(1).max(500),
})

export const CheckInsideOutHeadcountPayloadSchema = z.object({
  files: z.array(FileEntrySchema).min(1, 'At least one file is required'),
})

// ─── Stage result schemas (structural validation at IPC/HTTP boundaries) ──────
// z.custom preserves the TypeScript type while validating minimum structure.

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v)

export const Stage1ResultSchema = z.custom<Stage1Result>(
  (v) => isObj(v) && Array.isArray(v.subfunctionList) && Array.isArray(v.dataGaps),
  { message: 'Invalid Stage 1 result: expected { subfunctionList, dataGaps }' }
)
export const Stage2ResultSchema = z.custom<Stage2Result>(
  (v) => isObj(v) && Array.isArray(v.subfunctions) && Array.isArray(v.newWork),
  { message: 'Invalid Stage 2 result: expected { subfunctions, newWork }' }
)
export const Stage3ResultSchema = z.custom<Stage3Result>(
  (v) => isObj(v) && Array.isArray(v.subfunctions),
  { message: 'Invalid Stage 3 result: expected { subfunctions }' }
)
export const Stage4ResultSchema = z.custom<Stage4Result>(
  (v) => isObj(v) && Array.isArray(v.process_areas),
  { message: 'Invalid Stage 4 result: expected { process_areas }' }
)
export const Stage5ResultSchema = z.custom<Stage5Result>(
  (v) => isObj(v) && Array.isArray(v.automation_builds),
  { message: 'Invalid Stage 5 result: expected { automation_builds }' }
)
export const Stage6ResultSchema = z.custom<Stage6Result>(
  (v) => isObj(v) && Array.isArray(v.journey_narrative) && Array.isArray(v.closing_narrative),
  { message: 'Invalid Stage 6 result: expected { journey_narrative, closing_narrative }' }
)

const PrecomputedSchema = z.object({
  stage1: Stage1ResultSchema.optional(),
  stage2: Stage2ResultSchema.optional(),
  stage3: Stage3ResultSchema.optional(),
  stage4: Stage4ResultSchema.optional(),
  stage5: Stage5ResultSchema.optional(),
  stage6: Stage6ResultSchema.optional(),
}).optional()

export const GenerateInsideOutPayloadSchema = z.object({
  projectId: ProjectIdSchema,
  files: z.array(FileEntrySchema).min(1, 'At least one file is required'),
  clientName: z.string().min(1).max(500),
  industry: z.string().max(200).default(''),
  functionScope: z.array(z.string().min(1).max(200)),
  provider: AIProviderSchema,
  apiKey: z.string().default(''),
  branding: ClientBrandingSchema,
  mmsId: z.string().regex(/^\d{8}$/).optional(),
  purpose: z.string().max(2000).optional(),
  industryChoices: z.array(z.string().max(200)).optional(),
  precomputed: PrecomputedSchema,
})

export const RunInsideOutStagePayloadSchema = z.object({
  projectId: ProjectIdSchema,
  stageIndex: z.union([
    z.literal(1), z.literal(2), z.literal(3),
    z.literal(4), z.literal(5), z.literal(6),
  ]),
  files: z.array(FileEntrySchema),
  supplementaryFiles: z.array(FileEntrySchema).optional(),
  clientName: z.string().min(1).max(500),
  industry: z.string().max(200).default(''),
  functionScope: z.array(z.string().min(1).max(200)),
  provider: AIProviderSchema,
  apiKey: z.string().default(''),
  precomputed: PrecomputedSchema,
})

// ─── Tab 4 Standalone Regen ───────────────────────────────────────────────────

export const RegenTab4PayloadSchema = z.object({
  projectId: ProjectIdSchema,
  provider: AIProviderSchema,
  apiKey: z.string().default(''),
})

// ─── Feedback ────────────────────────────────────────────────────────────────

export const ApplyFeedbackPayloadSchema = z.object({
  projectId: ProjectIdSchema,
  feedback: z.string().min(1).max(10000),
  targetSubfunctionName: z.string().max(500).nullable(),
  clientName: z.string().min(1).max(500),
  industry: z.string().max(200).default(''),
  provider: AIProviderSchema,
  apiKey: ApiKeySchema,
  brandColor: z.string().optional(),
})

// ─── File Operations ─────────────────────────────────────────────────────────

export const FilePathSchema = z.string().min(1).refine(
  (path) => !path.includes('..'),
  'Path must not contain ".." segments'
)

export const PasswordSchema = z.string().min(1, 'Password is required').max(200)

/**
 * Shape check for a Templates-page download request. The handler still resolves
 * the id against TEMPLATE_CATALOG and 404s on a miss, so an unknown (but
 * well-shaped) id never touches the filesystem.
 */
export const TemplateIdSchema = z
  .string()
  .min(1, 'Template id is required')
  .max(128)
  .regex(/^[a-z0-9-]+$/, 'Invalid template id')

/** Payload for the Excel upload boundary (web `/files/parse-excel`, desktop `FILES_PARSE_EXCEL`). */
export const ParseExcelPayloadSchema = z.object({
  base64: z.string().min(1, 'Missing base64 field'),
  columnHints: z.record(z.string(), z.string()).optional(),
})

import {
  GccOutsourcingScenarioSchema,
  GccCombinableScenarioSchema,
  GccEligibilityRulesSchema,
  GccPayRateTableSchema,
  GccProgressEventSchema,
  GccJobStatusSchema,
} from './gcc/schemas'
import { base64DecodedBytes, gccTaxonomyRejection, isGccTaxonomyFile } from './gcc/uploads'
import { GCC_QUICK_WIN_HORIZON_MAX_MONTHS } from './gcc/quick-win'
export * from './gcc/schemas'

// ─── Project (for save) ──────────────────────────────────────────────────────

export const ProjectSchema = z.object({
  id: ProjectIdSchema,
  name: z.string().min(1).max(500),
  client: z.string().min(1).max(500),
  industry: z.string().max(200).optional().default(''),
  mode: z.enum(['outside-in', 'inside-out', 'gcc']),
  createdAt: z.string().optional().default(''),
  updatedAt: z.string().optional().default(''),
  branding: ClientBrandingSchema,
  profile: ClientProfileSchema.optional(),
  data: z.any().optional(), // DashboardData is complex and validated by the AI pipeline
  gccData: z.any().optional(), // GccDashboardData (incl. s0bReview) validated by the GCC pipeline
  gccError: z.string().optional(), // set when GCC generation fails
  gccRoleCount: z.number().nonnegative().optional(),
  gccLabourSavingUSD: z.number().optional(),
  // Precomputed summary counts — must be in schema so saveProject preserves them through PUT validation.
  subfunctionCount: z.number().nonnegative().optional(),
  automationBuildCount: z.number().nonnegative().optional(),
  totalFteCount: z.number().nonnegative().optional(),
  // null = clear persisted stage results (pipeline reset). Omitted = keep stored value.
  precomputed: z.null().optional(),
  mmsId: z.string().regex(/^\d{8}$/, 'MMS ID must be exactly 8 digits').optional(),
  purpose: z.string().max(2000).optional(),
  createdBy: z.email().optional(),
  // GCC ↔ TN bidirectional link fields — must be in schema so standard saveProject preserves them.
  sourceGccProjectId: z.string().optional(),
  gccScenario: GccOutsourcingScenarioSchema.optional(),
  gccTnError: z.string().optional(),
  gccTnFailedAt: z.string().optional(),
  gccTnJobId: z.string().optional(),
  derivedTnProjectIds: z.array(z.string()).optional(),
  gccCombined: z.object({
    retainedProjectId: z.string(),
    offshoreProjectId: z.string(),
    offshoreScenario: GccCombinableScenarioSchema,
    sourceUpdatedAt: z.object({ retained: z.string(), offshore: z.string() }),
    builtAt: z.string(),
  }).optional(),
})

// ─── GCC Design Engine ───────────────────────────────────────────────────────

export const GenerateGccPayloadSchema = z.object({
  projectId: ProjectIdSchema,
  clientName: z.string().min(1).max(500),
  industry: z.string().max(200).default(''),
  functionScope: z.array(z.string().min(1).max(200)),
  eligibilityRules: GccEligibilityRulesSchema,
  payRateTable: GccPayRateTableSchema.optional(),
  // WI 2048: whole percent of a task's work AI must release to keep it from migrating
  automateFirstThresholdPct: z.number().int().min(1).max(100).optional(),
  // WI 2806: whole months a low-complexity Automate / Agentify task must be live within to be a quick win.
  // WI 2886: 0–6, where 0 turns quick wins off. Stored designs (gccData) keep any older value.
  quickWinHorizonMonths: z.number().int().min(0).max(GCC_QUICK_WIN_HORIZON_MAX_MONTHS).optional(),
  provider: AIProviderSchema,
  apiKey: z.string().default(''),
  branding: ClientBrandingSchema,
  // Same type, empty and 20 MB rules as the Step 1 picker (WI 2332, WI 2363,
  // WI 2364): an .html, 0-byte or oversized upload used to pass here and fail later
  // with an unclear error. Like the picker, an unsupported type is the only error
  // reported. Type issues sit on `name`; empty and size issues on `base64`.
  uploadedFiles: z.array(z.object({
    base64: z.string(),
    name: z.string().min(1).max(500),
  }).superRefine((file, ctx) => {
    const rejection = gccTaxonomyRejection(file.name, base64DecodedBytes(file.base64))
    if (!rejection) return
    const path = isGccTaxonomyFile(file.name) ? ['base64'] : ['name']
    ctx.addIssue({ code: 'custom', path, message: rejection })
  })).optional(),
})

export const CombineGccTnPayloadSchema = z.object({
  gccProjectId: ProjectIdSchema,
  offshoreScenario: GccCombinableScenarioSchema,
})

export const RunTnOnRetainedPayloadSchema = z.object({
  gccProjectId: ProjectIdSchema,
  scenario: GccOutsourcingScenarioSchema,
  provider: AIProviderSchema,
  apiKey: z.string().default(''),
  branding: ClientBrandingSchema,
})

export const GccJobStateSchema = z.object({
  id: JobIdSchema,
  projectId: ProjectIdSchema,
  status: GccJobStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().optional(),
  error: z.string().optional(),
  progress: z.array(GccProgressEventSchema),
})

// ─── Test Connection ─────────────────────────────────────────────────────────

export const TestConnectionSchema = z.object({
  provider: AIProviderSchema,
  apiKey: ApiKeySchema,
})

// ─── License ────────────────────────────────────────────────────────────────

export const LicenseFeatureSchema = z.enum(['outside-in', 'inside-out', 'export'])

export const LicenseSchema = z.object({
  id: z.uuid(),
  recipientName: z.string().min(1).max(500),
  recipientEmail: z.email(),
  organizationName: z.string().min(1).max(500),
  machineFingerprint: z.string().max(128),
  issuedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  licenseType: z.enum(['full', 'trial']).optional().default('full'),
  features: z.array(LicenseFeatureSchema).min(1),
})

export const SignedLicenseSchema = z.object({
  payload: z.string().min(1),
  signature: z.string().min(1),
})

export const ActivateLicensePayloadSchema = z.object({
  licenseData: z.string().min(1, 'License data is required'),
})
